// ./core/Web/types/StompMessage.ts

export interface StompTick {
  type: 'TICK';
  multiplier: number;
  ts: number;
  roundId: number;
}
export interface StompRoundSummary {
  type: 'ROUND_SUMMARY';
  bust: number;
  roundNo: number;
  serverSeed?: string;
  serverSeedHash?: string;
  /** Event time from STOMP (ms or seconds). */
  ts?: number;
  endedAt?: number;
}
export interface StompSnapshot {
  op: 'SNAPSHOT';
  p: { round: { phase: 'LOBBY' | string; roundId: string } };
}
export interface StompError {
  op: 'ERROR';
  p: { message: string; betNo?: number };
}

// NEW: Added StompPhaseUpdate interface
export interface StompPhaseUpdate {
  op: 'PHASE_UPDATE';
  p: {
    roundId: number;
    roundNo?: number;
    phase:
      | 'LOBBY'
      | 'WAITING'
      | 'BET_LOCK'
      | 'TAKEOFF'
      | 'FLYING'
      | 'RUNNING'
      | 'FLEW_AWAY'
      | 'REVEAL'
      | 'CRASHED'
      | 'COMPLETED';
    phaseStart: number;
    phaseEnd: number;
    bust?: number; // Included for FLEW_AWAY phase
  };
}

export interface StompGenericEvent {
  type: string;
  [key: string]: any;
}

export interface StompAllActiveRounds {
  type: 'ALL_ACTIVE_ROUNDS';
  entries: any[];
}

export interface StompActiveBetPlayers {
  type: 'ACTIVE_BET_PLAYERS';
  activePlayers: number;
}

export interface StompTotalCashoutAmount {
  type: 'TOTAL_CASHOUT_AMOUNT';
  totalCashout: number;
}

export interface StompActiveVsTotalBetsCount {
  type: 'ACTIVE_VS_TOTAL_BETS_COUNT';
  active: number;
  total: number;
}

export interface StompRoundUpdate {
  op: 'ROUND_UPDATE';
  v?: string;
  ts?: string;
  p: {
    roundId?: string | number;
    roundNo?: number;
    [key: string]: unknown;
  };
}

// Chat message types
export interface StompChatHistory {
  count: number;
  messages: Array<{
    user: { nickName: string; roles: string[] };
    type: 'CHAT';
    content: { text: string };
    meta: { timestamp: number; clientSeq: string };
  }>;
  timestamp: number;
}

export interface StompChatMessage {
  user: { nickName: string; roles: string[] };
  type: 'CHAT';
  content: {
    text: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
  };
  meta: {
    timestamp: number;
    clientSeq: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
  };
}

export interface StompChatLikeUpdate {
  type: 'CHAT_LIKE' | 'CHAT_REACTION';
  content?: {
    clientSeq?: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
  };
  meta?: {
    clientSeq?: string;
    likeCount?: number;
    likes?: number;
    likedByMe?: boolean;
    liked?: boolean;
  };
}
export interface StompJoinAck {
  op: 'JOIN_ACK';
  p: {
    currency?: string;
    balance?: number;
    tenant?: string | number;
    tenantId?: string | number;
    game?: string | number;
    gameId?: string | number;
    table?: string | number;
    tableId?: string | number;
    roundId?: string | number;
    roundNo?: number;
    phase?: string;
    phaseTiming?: {
      serverNow?: number;
      phaseStart?: number;
      phaseEnd?: number;
      lobbyEnd?: number;
      betLockEnd?: number;
      takeoffEnd?: number;
      revealEnd?: number;
      [key: string]: unknown;
    };
    currentMbp?: number;
    currentMultiplier?: number;
    currentX?: number;
    serverNow?: number;
    phaseStart?: number;
    phaseEnd?: number;
    myRoundBets?: unknown[];
    myBets?: unknown[];
    autoCashout?: number | boolean;
    cashoutMultiplier?: number;
    cashoutAmount?: number;
    roundHistory?: unknown[];
    liveBets?: unknown[];
    liveMetrics?: {
      activeBetPlayers?: number;
      activeBets?: number;
      totalBets?: number;
      totalCashoutAmount?: number;
      [key: string]: unknown;
    };
    activeBetPlayers?: number;
    activeBets?: number;
    totalBets?: number;
    totalCashoutAmount?: number;
    bust?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

interface BaseAckResponse {
  request: string;
  status: 'OK' | 'ERROR';
}

// MODIFIED: Updated PlaceBetAck to match the new direct ACK response
export interface PlaceBetAck extends BaseAckResponse {
  request: 'PLACE_BET';
  data: {
    balance: number;
    betId: string;
    betNo: number; // Changed from betSlot to betNo
    currency: string;
    roundId: number;
    status: 'PLACED';
  };
}

// Updated the CashoutAck interface to match the auto-cashout payload structure.
export interface CashoutAck extends BaseAckResponse {
  request: 'CASHOUT';
  data: {
    atX: number;
    balance: number;
    betNo: number;
    betId: string;
    payout: number;
    currency: string;
    roundId: number;
  };
}

// NEW: Added CancelBetAck to handle cancel responses
export interface CancelBetAck extends BaseAckResponse {
  request: 'CANCEL' | 'CANCEL_BET'; // Support both forms
  data: {
    balance: number;
    betId: string;
    betNo: number;
    currency: string;
    roundId: number;
    status: 'CANCELLED';
    refunded: number;
  };
}

// MODIFIED: Added CancelBetAck to the union type
export type StompAck = PlaceBetAck | CashoutAck | CancelBetAck;

export interface StompGenericAck {
  v: '1';
  op: 'ACK';
  id: string;
  p: {
    code: 'OK' | 'ERROR';
    data: {
      result?: {
        betNo: number;
        [key: string]: any;
      };
      message?: string;
      [key: string]: any;
    };
  };
}

// Payloads sent from the client
// MODIFIED: Added optional autoCashoutX property
export interface PlaceBetPayload {
  v: '1';
  op: 'PLACE_BET';
  id: string;
  p: {
    stakeChips: number;
    betId: number;
    autoCashoutX?: number; // Value is sent only if auto cashout is enabled
  };
  idem: string;
  clientSeed: string;
  clientNonce: number;
}

export interface CashoutPayload {
  v: '1';
  op: 'CASHOUT_NOW';
  id: string;
  p: { betId: number; expectAt: number };
  idem: string;
}

export interface CancelBetPayload {
  v: '1';
  op: 'CANCEL_BET';
  id: string;
  p: { betId: number };
}

export interface PingPayload {
  op: 'PING';
  clientTime: number;
  playerId: string | null;
}

// Updated PongResponse to match the server's actual message structure
export interface PongResponse {
  type: 'PONG';
  gameId: number;
  ts: number;
  tableId: number;
}

export type StompMessage =
  | StompTick
  | StompRoundSummary
  | StompSnapshot
  | StompAck
  | StompError
  | StompGenericAck
  | StompGenericEvent
  | StompJoinAck
  | PongResponse
  | StompAllActiveRounds
  | StompActiveBetPlayers
  | StompTotalCashoutAmount
  | StompActiveVsTotalBetsCount
  | StompChatHistory
  | StompChatMessage
  | StompChatLikeUpdate
  | StompPhaseUpdate; // NEW: Added to union type

// Type guards
export function isStompTick(msg: any): msg is StompTick {
  return msg?.type === 'TICK' && typeof msg.multiplier === 'number';
}
export function isStompRoundSummary(msg: any): msg is StompRoundSummary {
  return msg?.type === 'ROUND_SUMMARY' && typeof msg.bust === 'number';
}
export function isStompSnapshot(msg: any): msg is StompSnapshot {
  return msg?.op === 'SNAPSHOT' && msg.p?.round?.phase === 'LOBBY';
}
// NEW: Added type guard for phase updates
export function isStompPhaseUpdate(msg: any): msg is StompPhaseUpdate {
  return msg?.op === 'PHASE_UPDATE' && typeof msg.p?.phase === 'string';
}
export function isStompAck(msg: any): msg is StompAck {
  return typeof msg?.request === 'string' && typeof msg?.status === 'string';
}
export function isStompError(msg: any): msg is StompError {
  return msg?.op === 'ERROR';
}
export function isStompGenericAck(msg: any): msg is StompGenericAck {
  return msg?.op === 'ACK' && typeof msg.p?.code === 'string';
}
export function isStompJoinAck(msg: any): msg is StompJoinAck {
  return msg?.op === 'JOIN_ACK';
}
// Updated the type guard to check for `type: "PONG"`
export function isStompPong(msg: any): msg is PongResponse {
  return msg?.type === 'PONG';
}

// Type guards for scoreboard events
export function isStompAllActiveRounds(msg: any): msg is StompAllActiveRounds {
  return msg?.type === 'ALL_ACTIVE_ROUNDS' && Array.isArray(msg.entries);
}
export function isStompActiveBetPlayers(
  msg: any
): msg is StompActiveBetPlayers {
  return (
    msg?.type === 'ACTIVE_BET_PLAYERS' && typeof msg.activePlayers === 'number'
  );
}
export function isStompTotalCashoutAmount(
  msg: any
): msg is StompTotalCashoutAmount {
  return (
    msg?.type === 'TOTAL_CASHOUT_AMOUNT' && typeof msg.totalCashout === 'number'
  );
}
export function isStompActiveVsTotalBetsCount(
  msg: any
): msg is StompActiveVsTotalBetsCount {
  return (
    msg?.type === 'ACTIVE_VS_TOTAL_BETS_COUNT' &&
    typeof msg.active === 'number' &&
    typeof msg.total === 'number'
  );
}
export function isStompRoundUpdate(msg: any): msg is StompRoundUpdate {
  return msg?.op === 'ROUND_UPDATE';
}

// Chat type guards
export function isStompChatHistory(msg: any): msg is StompChatHistory {
  return (
    msg &&
    typeof msg.count === 'number' &&
    Array.isArray(msg.messages) &&
    typeof msg.timestamp === 'number'
  );
}
export function isStompChatMessage(msg: any): msg is StompChatMessage {
  return msg && msg.type === 'CHAT' && msg.user && msg.content && msg.meta;
}
export function isStompChatLikeUpdate(msg: any): msg is StompChatLikeUpdate {
  return (
    msg &&
    (msg.type === 'CHAT_LIKE' || msg.type === 'CHAT_REACTION') &&
    (msg.content || msg.meta)
  );
}
